<template>
  <div class="chat-view">
    <div class="ambient-orb" aria-hidden="true"></div>
    <welcome-top-bar
      :is-fullscreen="isFullscreen"
      :is-customized="isCustomized"
      :is-wide="isWide"
      :is-docked="isDocked"
      :device-type="deviceType"
      @home="$emit('go-home')"
      @new-chat="$emit('new-chat')"
      @show-history="$emit('show-history')"
      @toggle-history="$emit('toggle-history')"
      @toggle-fullscreen="$emit('toggle-fullscreen')"
      @reset-size="$emit('reset-size')"
      @close="$emit('close')"
      @toggle-dock="$emit('toggle-dock')"
      @start-popover-drag="$emit('start-popover-drag', $event)"
      @toggle-nav-sidebar="$emit('toggle-nav-sidebar')"
      @toggle-dark-mode="$emit('toggle-dark-mode', $event)"
    />

    <div class="chat-body">
      <history-sidebar
        :is-open="isHistoryOpen"
        :overlay="isOverlay"
        @close="$emit('close-history')"
        @select-chat="$emit('select-chat-from-history', $event)"
        @new-chat="$emit('new-chat-from-history')"
      />

      <div class="chat-main">
        <div class="messages-wrapper">
          <!-- Ambient reminder that answers are scoped to the open patient.
               A persistent header ABOVE the scroll area (not inside it) so it
               always shows and never overlaps the first message. The
               explanatory tail is dropped on a narrow panel to stay one line. -->
          <div v-if="patientModeEnabled" class="patient-mode-banner">
            <i class="fas fa-user-lock"></i>
            <span>
              {{ $filters.customerLabel("singular", true) }} mode ·
              <strong>{{
                patientCapsuleLabel ||
                `this ${$filters.customerLabel("singular", false)}`
              }}</strong>
            </span>
          </div>

          <div ref="messagesContainer" class="messages-container">
            <div
              v-if="messagesLoading && messages.length === 0"
              class="loading-state"
            >
              <i class="fas fa-circle-notch fa-spin"></i>
              <p>Loading messages...</p>
            </div>

            <div
              v-else-if="!messagesLoading && messages.length === 0"
              class="empty-state"
            >
              <div class="empty-orb">
                <orb :size="90" :colors="orbColors" />
              </div>
              <h3>Start a Conversation</h3>
              <p>Type a message below to begin chatting with Oliver</p>
            </div>

            <!-- Messages List -->
            <div v-else class="messages-list">
              <div v-if="hasMoreMessages" class="load-more-section">
                <button
                  class="load-more-button"
                  @click="loadMoreMessages"
                  :disabled="messagesLoading"
                >
                  <i
                    v-if="messagesLoading"
                    class="fas fa-circle-notch fa-spin"
                  ></i>
                  <span v-else>Load More</span>
                </button>
              </div>

              <message-bubble
                v-for="message in messages"
                :key="message.id"
                :message="message"
                :user-photo="userAvatarSrc"
                :conversation-id="selectedChat?.id"
                :is-streaming="
                  (isStreamingContent || isWaitingForResponse) &&
                  message.id === lastMessage?.id
                "
                :is-last="message.id === lastMessage?.id"
                @interaction-answered="resumeFollowing"
                @follow-up="askFollowUp"
                @stop-following="isAtBottom = false"
                @open-image="lightbox = $event"
                @open-table="tableView = $event"
              />
            </div>
          </div>

          <!-- Scroll to Bottom Button -->
          <scroll-to-bottom-button
            :container="$refs.messagesContainer"
            :smooth="!isWaitingForResponse && !isStreamingContent"
            @click="isAtBottom = true"
          />
        </div>

        <div class="chat-input-area">
          <div
            class="chat-input-wrap"
            :class="{ 'chat-input-wrap--expanded': isChatInputExpanded }"
          >
            <div class="chat-input-inner">
              <chat-input
                :key="selectedChat?.id"
                ref="chatInput"
                inline
                :disabled="isInputDisabled"
                :queue-full="isQueueFull"
                :is-waiting-for-response="isAwaitingReply"
                :is-transcribing="isTranscribing"
                :placeholder="chatInputPlaceholder"
                :show-patient-mode="showPatientMode"
                :patient-mode-active="patientModeEnabled"
                :patient-mode-btn-label="patientModeBtnLabel"
                :can-switch-patient="canSwitchPatient"
                :patient-capsule-label="patientCapsuleLabel"
                :show-web-search="true"
                :web-search-active="webSearchEnabled"
                :suggestions-open="showSuggestionsPanel"
                :attachment-drafts="attachmentDrafts"
                :attachment-notices="attachmentNotices"
                :attachments-creating="attachmentsCreating"
                @send="handleSendMessage"
                @files-selected="handleFilesSelected"
                @remove-attachment="handleRemoveAttachment"
                @retry-attachment="handleRetryAttachment"
                @dismiss-notices="handleDismissNotices"
                @audio-recorded="handleAudioRecorded"
                @toggle-suggestions="
                  showSuggestionsPanel = !showSuggestionsPanel
                "
                @toggle-patient-mode="handleTogglePatientMode"
                @switch-patient-mode="$emit('switch-patient-mode')"
                @toggle-web-search="handleToggleWebSearch"
                @toggle-expand="isChatInputExpanded = $event"
              />
              <p class="disclaimer-note">
                Oliver can make mistakes. Always verify with clinical judgement.
              </p>
            </div>
          </div>
        </div>

        <suggestions-panel
          :is-open="showSuggestionsPanel"
          :is-fullscreen="isFullscreen"
          :is-wide="isWide"
          :patient-mode-enabled="patientModeEnabled"
          mode="chat"
          @populate-input="handlePopulateInput"
          @select-command="handleCommandSelect"
          @close="showSuggestionsPanel = false"
          @close-oliver="$emit('close')"
        />
      </div>

      <queue-sidebar
        :is-open="isQueueOpen"
        :queue="messageQueue"
        :paused="isQueuePaused"
        :overlay="isOverlay"
        :activity="activity"
        @close="isQueueOpen = false"
        @toggle="isQueueOpen = !isQueueOpen"
        @remove="
          (i) =>
            $store.dispatch('oliverChatBotV2/removeQueuedMessage', {
              chatId: selectedChat.id,
              index: i,
            })
        "
        @toggle-pause="handleToggleQueuePause"
      />
    </div>

    <table-lightbox
      v-if="tableView"
      :title="tableView.title"
      :html="tableView.html"
      :note="tableView.note"
      @close="tableView = null"
      @cite="citeFromTable"
    />
    <image-lightbox
      v-if="lightbox"
      :url="lightbox.url"
      :alt="lightbox.alt"
      :artifact-id="lightbox.artifactId"
      @close="lightbox = null"
    />
  </div>
</template>

<script>
import ChatInput from "@/components/OliverBotV2/chat/ChatInput.vue";
import MessageBubble from "@/components/OliverBotV2/chat/MessageBubble/MessageBubble.vue";
import HistorySidebar from "@/components/OliverBotV2/shared/HistorySidebar.vue";
import ImageLightbox from "@/components/OliverBotV2/shared/ImageLightbox.vue";
import TableLightbox from "@/components/OliverBotV2/shared/TableLightbox.vue";
import Orb from "@/components/OliverBotV2/shared/Orb.vue";
import QueueSidebar from "@/components/OliverBotV2/shared/QueueSidebar.vue";
import { SuggestionsPanel } from "@/components/OliverBotV2/suggestions";
import { getThemeOrbColors } from "@/components/OliverBotV2/theme/themes.js";
import { NARROW_PANEL_WIDTH } from "@/components/OliverBotV2/utils/panelLayout.js";
import { getPatientPlaceholder } from "@/components/OliverBotV2/utils/patientModeManager.js";
import WelcomeTopBar from "@/components/OliverBotV2/views/welcome/WelcomeTopBar.vue";
import ScrollToBottomButton from "@/components/ScrollToBottomButton.vue";
import attachmentHandling from "@/components/OliverBotV2/mixins/attachmentHandling.js";
import scrollToBottom from "@/mixins/scrollToBottom.js";
import { patientContextMetadata } from "@/store/oliverChatBotV2.js";
import { mapActions, mapGetters, mapState } from "vuex";
import { PENDING_REPLY_ID } from "@/store/oliverChatBotV2";

// How close to the bottom still counts as "following". The old 5px was tight
// enough that a sub-pixel rounding or a one-line reflow read as the reader
// having deliberately scrolled away.
const BOTTOM_THRESHOLD_PX = 48;

export default {
  name: "ChatView",
  components: {
    WelcomeTopBar,
    MessageBubble,
    ChatInput,
    SuggestionsPanel,
    HistorySidebar,
    QueueSidebar,
    Orb,
    ScrollToBottomButton,
    ImageLightbox,
    TableLightbox,
  },
  mixins: [scrollToBottom, attachmentHandling],
  props: {
    initialMessage: {
      type: Object,
      default: null,
    },
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    isWide: {
      type: Boolean,
      default: true,
    },
    isCustomized: {
      type: Boolean,
      default: false,
    },
    isDocked: {
      type: Boolean,
      default: false,
    },
    patientModeEnabled: {
      type: Boolean,
      default: false,
    },
    showPatientMode: {
      type: Boolean,
      default: false,
    },
    patientModeBtnLabel: {
      type: String,
      default: "Client Mode",
    },
    canSwitchPatient: {
      type: Boolean,
      default: false,
    },
    patientCapsuleLabel: {
      type: String,
      default: null,
    },
    webSearchEnabled: {
      type: Boolean,
      default: false,
    },
    isHistoryOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSuggestionsPanel: false,
      isAtBottom: true,
      lastScrollTop: 0,
      scrollObserver: null,
      isQueueOpen: false,
      panelWidth: Infinity,
      isChatInputExpanded: false,
      lightbox: null,
      tableView: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.attachScrollListener();
      this.setupAutoScroll();
    });
    this._panelRo = new ResizeObserver(([entry]) => {
      this.panelWidth = entry.contentRect.width;
    });
    this._panelRo.observe(this.$el);
  },
  beforeUnmount() {
    this.detachScrollListener();
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    if (this._panelRo) {
      this._panelRo.disconnect();
    }
  },
  computed: {
    ...mapState({
      selectedChat: (state) => state.oliverChatBotV2.selectedChat,
      isCreatingChat: (state) => state.oliverChatBotV2.isCreatingChat,
      isTranscribing: (state) => state.oliverChatBotV2.isTranscribing,
      patientMode: (state) => state.oliverChatBotV2.patientMode,
      deviceType: (state) => state.settings.deviceType,
      authUser: (state) => state.auth.user,
      oliverTheme: (state) => state.oliverChatBotV2.theme,
    }),
    ...mapGetters("oliverChatBotV2", {
      messages: "currentMessages",
      messagesLoading: "currentMessagesLoading",
      isWaitingForResponse: "currentIsWaitingForResponse",
      isStreamingContent: "currentIsStreamingContent",
      isPendingReconnect: "currentIsPendingReconnect",
      nextCursor: "currentNextCursor",
      messageQueue: "currentMessageQueue",
      isQueuePaused: "currentIsQueuePaused",
      activity: "currentActivity",
    }),
    // Switch sidebars to overlay (float over content) when the panel is
    // too narrow to push — 580px = 300px sidebar + 280px minimum chat area.
    // This fires on any resize: small window, fullscreen on small monitor, etc.
    isOverlay() {
      return this.panelWidth < NARROW_PANEL_WIDTH;
    },
    orbColors() {
      return getThemeOrbColors(this.oliverTheme);
    },
    hasMoreMessages() {
      return this.nextCursor !== null;
    },
    lastMessage() {
      return this.messages.length > 0
        ? this.messages[this.messages.length - 1]
        : null;
    },
    /**
     * A reply is on its way, whether this tab is streaming it or only watching
     * for one that outlived a refresh. Everything about the composer — the
     * button state, queueing, releasing the queue — should treat the two the
     * same; only the live turn has a stream attached, and none of them care.
     */
    isAwaitingReply() {
      return this.isWaitingForResponse || this.isPendingReconnect;
    },
    isInputDisabled() {
      return this.isCreatingChat || this.isTranscribing || !this.selectedChat;
    },
    isQueueFull() {
      return false;
    },
    chatInputPlaceholder() {
      if (this.patientModeEnabled && this.patientMode) {
        return getPatientPlaceholder(this.patientMode);
      }
      return "Ask anything";
    },
    userAvatarSrc() {
      return (this.authUser && this.authUser.profile_picture) || null;
    },
    // True while the latest turn is actively working (has steps, not finished).
    // Drives the drawer auto-open so the Activity board is visible as it runs.
    // A pure direct reply (no tool steps) never flips this, so the drawer only
    // pops when there is real activity to show.
    isActivityRunning() {
      return Boolean(
        this.activity && !this.activity.done && this.activity.steps?.length > 0
      );
    },
  },
  watch: {
    selectedChat: {
      immediate: true,
      handler(newChat) {
        if (newChat) {
          this.loadMessages();
        }
      },
    },
    // Mutual exclusion only when the panel is too narrow for both sidebars at once.
    // Below 950px: opening one closes the other.
    // 950px+: both can be open freely.
    // Also enforced when the panel shrinks below 950px while both are already open.
    panelWidth(newVal) {
      if (newVal < 950 && this.isHistoryOpen && this.isQueueOpen) {
        this.isQueueOpen = false;
      }
    },
    isHistoryOpen(val) {
      if (val && this.panelWidth < 950) {
        this.isQueueOpen = false;
      }
    },
    isQueueOpen(val) {
      if (val && this.panelWidth < 950 && this.isHistoryOpen) {
        this.$emit("close-history");
      }
    },
    "messageQueue.length"(newLen, oldLen) {
      if (newLen > 0 && oldLen === 0 && !this.isOverlay) {
        this.isQueueOpen = true;
      }
    },
    // Auto-open the drawer when a turn starts working, so the user actually
    // sees the live Activity board (its whole reason for being in the sidebar).
    isActivityRunning(running) {
      if (running && !this.isOverlay) {
        this.isQueueOpen = true;
      }
    },
  },
  methods: {
    /** Full screen closes, and the answer the table belongs to opens its sources there. */
    citeFromTable(n) {
      const view = this.tableView;
      this.tableView = null;
      view?.cite?.(n);
    },
    ...mapActions("oliverChatBotV2", [
      "getChatMessages",
      "sendChatMessage",
      "establishSSEConnection",
      "storeChatMessage",
      "updateChatMessage",
      "generateChatTitle",
      "transcribeAudio",
      "resumePendingGeneration",
    ]),
    async loadMessages() {
      if (!this.selectedChat) return;

      // Skip loading if messages already exist or are currently being loaded
      // This prevents unnecessary API calls and preserves optimistic UI updates
      if (this.messages.length > 0 || this.messagesLoading) {
        this.$nextTick(() => {
          this.scrollToBottom(false);
        });
        this.resumeIfGenerating();
        return;
      }

      await this.getChatMessages({
        chatId: this.selectedChat.id,
        limit: 50,
      });

      this.$nextTick(() => {
        this.scrollToBottom(false);
      });

      this.resumeIfGenerating();

      // If there's an initial message and no existing messages, send it automatically
      if (this.initialMessage && this.messages.length === 0) {
        await this.handleSendMessage(this.initialMessage);
      }
    },
    /**
     * A conversation whose last message is still the user's may have a reply
     * generating right now: generation outlives the tab it was started in, so
     * reopening one is not proof that nothing is happening.
     */
    resumeIfGenerating() {
      if (!this.selectedChat) return;
      const last = this.lastMessage;
      // Nothing has answered yet, or our own placeholder is still up. Re-attaching
      // is a no-op while the watch is alive and repairs it if it somehow is not,
      // so this stays right however the user got back here.
      if (last?.role !== "user" && last?.id !== PENDING_REPLY_ID) return;
      this.resumePendingGeneration({ chatId: this.selectedChat.id });
    },
    async loadMoreMessages() {
      if (!this.selectedChat || !this.nextCursor) return;

      const previousScrollHeight = this.$refs.messagesContainer.scrollHeight;

      await this.getChatMessages({
        chatId: this.selectedChat.id,
        limit: 50,
        cursor: this.nextCursor,
      });

      this.$nextTick(() => {
        const newScrollHeight = this.$refs.messagesContainer.scrollHeight;
        this.$refs.messagesContainer.scrollTop =
          newScrollHeight - previousScrollHeight;
      });
    },
    async handleSendMessage(payload) {
      const messageText = payload.text || "";

      // Every file is bound to the message whatever state it is in: Oliver waits for the
      // ones still being read, and names any it could not read.
      const chatId = this.selectedChat?.id;
      if (
        !this.selectedChat ||
        (!messageText.trim() &&
          (payload.attachDrafts === false || !this.attachmentDrafts.length))
      )
        return;
      // The composer clears at once; the files travel with the message and stay live.
      const attachments =
        payload.attachDrafts === false
          ? []
          : await this.$store.dispatch(
              "oliverChatBotV2/takeComposerAttachments"
            );
      const attachmentIds = attachments.map((a) => a.id);

      // Queue the message if Oliver is currently producing a reply.
      if (this.isAwaitingReply) {
        this.$store.commit("oliverChatBotV2/ENQUEUE_MESSAGE", {
          chatId: this.selectedChat.id,
          message: {
            text: messageText,
            attachment_ids: attachmentIds,
            attachments,
            patientModeSnapshot: this.$store.state.oliverChatBotV2.patientMode
              ? { ...this.$store.state.oliverChatBotV2.patientMode }
              : null,
          },
        });
        return;
      }

      const userMessageId = `user-${Date.now()}`;

      // First message only; intentionally not gated on the title string so a
      // reused conversation with a stray placeholder title still gets titled.
      const shouldGenerateTitle = this.messages.length === 0;

      this.isAtBottom = true;
      this.scrollToBottom(false);

      // Same scoping the server gets — otherwise this reads as client mode off the moment
      // it lands, dropping the client before the request is built.
      const patientMode = this.$store.state.oliverChatBotV2.patientMode;

      await this.storeChatMessage({
        chatId,
        message: {
          id: userMessageId,
          role: payload.role || "user",
          content: messageText,
          mode: patientMode?.id ? "patient" : null,
          context_metadata: patientContextMetadata(patientMode),
          attachments,
          created_at: new Date().toISOString(),
        },
      });

      const response = await this.sendChatMessage({
        chatId,
        userMessageId,
        generateTitle: shouldGenerateTitle,
        data: {
          content: messageText,
          attachment_ids: attachmentIds,
        },
      });

      if (response) {
        if (
          response.data &&
          response.data.data &&
          response.data.data.attachments
        ) {
          await this.updateChatMessage({
            chatId,
            updates: {
              id: userMessageId,
              attachments: response.data.data.attachments,
            },
          });
        }
      }
    },
    async handleAudioRecorded(audioFile) {
      if (!this.selectedChat) return;

      try {
        const response = await this.transcribeAudio({ file: audioFile });

        if (response && response.transcription) {
          if (this.$refs.chatInput) {
            this.$refs.chatInput.setInputValue(response.transcription);
          }
        }
      } catch (error) {
        console.error("Audio transcription failed:", error);
      }
    },
    handleToggleQueuePause() {
      const newPaused = !this.isQueuePaused;
      this.$store.commit("oliverChatBotV2/SET_QUEUE_PAUSED", {
        chatId: this.selectedChat.id,
        value: newPaused,
      });
      // If resuming and Oliver is already idle, fire the next queued message now
      if (!newPaused && !this.isAwaitingReply && this.messageQueue.length > 0) {
        this.$store.dispatch("oliverChatBotV2/dequeueAndSend", {
          chatId: this.selectedChat.id,
        });
      }
    },
    handleTogglePatientMode(isActive) {
      this.$emit("toggle-patient-mode", isActive);
    },

    handleToggleWebSearch(isActive) {
      this.$emit("toggle-web-search", isActive);
    },
    handlePopulateInput(prompt) {
      if (this.$refs.chatInput) {
        this.$refs.chatInput.setInputValue(prompt, { cursorAtStart: true });
      }
    },
    handleCommandSelect(command) {
      if (this.$refs.chatInput) {
        this.$refs.chatInput.setInputValue(command.prompt, {
          cursorAtStart: true,
        });
      }
    },
    setupAutoScroll() {
      const container = this.$refs.messagesContainer;

      if (!container) {
        console.warn("Messages container not found for MutationObserver");
        return;
      }

      this.scrollObserver = new MutationObserver(this.followTail);

      // Observe the entire messages container
      this.scrollObserver.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // An image reaching its natural height changes the scroll height without
      // touching the DOM, so the observer above never hears about it and the
      // answer scrolls out of view exactly when a chart appears. `load` does not
      // bubble — hence capture.
      container.addEventListener("load", this.followTail, true);
    },
    /** Re-attach to the bottom after a deliberate "show me what's next" action. */
    resumeFollowing() {
      this.isAtBottom = true;
      this.followTail();
    },
    askFollowUp(text) {
      this.resumeFollowing();
      this.handleSendMessage({ text, attachDrafts: false });
    },
    /** Keep the newest content in view, unless the reader has scrolled away. */
    followTail() {
      if (!this.isAtBottom) return;
      const container = this.$refs.messagesContainer;
      if (!container) return;
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
        this.lastScrollTop = container.scrollTop;
      });
    },
    attachScrollListener() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.addEventListener(
          "scroll",
          this.handleScroll
        );
      }
    },
    detachScrollListener() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.removeEventListener(
          "scroll",
          this.handleScroll
        );
        this.$refs.messagesContainer.removeEventListener(
          "load",
          this.followTail,
          true
        );
      }
    },
    /**
     * Following stops when the READER moves up, not when the content grows.
     *
     * This used to be `distanceFromBottom < 5` on every scroll event, which
     * conflated the two: an image finishing its load adds its full height at
     * once, so the very next event measured hundreds of pixels from the bottom
     * and following switched itself off for the rest of the turn — while the
     * reader had not touched anything. Same for an interaction card appearing.
     *
     * So the only thing that detaches is scrollTop actually DECREASING, and
     * returning to within a comfortable distance of the bottom re-attaches.
     */
    handleScroll() {
      const container = this.$refs.messagesContainer;
      if (!container) return;

      const scrollTop = container.scrollTop;
      const distanceFromBottom =
        container.scrollHeight - scrollTop - container.clientHeight;
      const movedUp = scrollTop < this.lastScrollTop - 1;
      this.lastScrollTop = scrollTop;

      if (distanceFromBottom <= BOTTOM_THRESHOLD_PX) {
        this.isAtBottom = true;
      } else if (movedUp) {
        this.isAtBottom = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-view {
  --oliver-content-max: 1800px;

  width: 100%;
  height: 100%;
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  background: var(--oliver-gradient);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  transition: background 0.3s ease;

  .ambient-orb {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 35%;
    aspect-ratio: 1;
    background: radial-gradient(
      circle,
      rgba(210, 160, 120, 0.2) 0%,
      rgba(240, 200, 160, 0.1) 55%,
      transparent 75%
    );
    border-radius: 50%;
    filter: blur(24px);
    pointer-events: none;
    z-index: 0;
  }
}

.chat-body {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

.chat-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.chat-input-area {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.chat-input-wrap {
  padding: 8px 24px 4px;
  display: flex;
  justify-content: center;

  &--expanded {
    padding-left: 0;
    padding-right: 0;
  }
}

.chat-input-inner {
  width: 100%;
  max-width: var(--oliver-content-max);
  margin: 0 auto;
}

.oliver-disclaimer {
  margin: 0;
  padding: 6px 0 4px;
  text-align: right;
  font-size: 0.8rem;
  color: var(--oliver-text-muted);
}

.oliver-disclaimer-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  text-decoration-color: var(--oliver-accent-border);
  cursor: pointer;

  &:hover {
    text-decoration-color: var(--oliver-accent);
  }
}

.messages-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: transparent;
  display: flex;
  flex-direction: column;
  container-type: inline-size;
}

.patient-mode-banner {
  flex-shrink: 0;
  display: flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 6px;
  // Centered pill — being centered also keeps it clear of the queue-drawer
  // toggle that floats at the right edge (.queue-edge-toggle-btn, top:16px).
  margin: 4px auto 8px;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.3;
  text-align: center;
  color: var(--oliver-text, #5c5148);
  // OPAQUE fill: messages (incl. dark-brown user bubbles) scroll under this
  // sticky pill, so a translucent background bled through and killed the text
  // contrast. A solid fill keeps the label readable over anything behind it.
  background: var(--oliver-chip-bg, #f6efe7);
  border: 1px solid rgba(120, 90, 60, 0.22);
  box-shadow: 0 1px 3px rgba(90, 60, 30, 0.12);

  i {
    font-size: 11px;
    opacity: 0.85;
  }

  strong {
    font-weight: 600;
  }
}

.messages-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: 0 24px;
  overflow-y: auto;
  overflow-x: hidden;
  background: transparent;
  scrollbar-width: thin;
  scrollbar-color: var(--oliver-scrollbar) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--oliver-scrollbar);
    border-radius: 3px;

    &:hover {
      background: var(--oliver-accent-border);
    }
  }
}

.messages-list {
  padding: 20px 0 0;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--oliver-content-max);
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
}

.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--oliver-text-muted);

  i {
    font-size: 32px;
    margin-bottom: 16px;
  }

  p {
    font-size: 15px;
    margin: 0;
  }
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  animation: fadeIn 0.4s ease;

  .empty-orb {
    width: 90px;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: var(--oliver-text);
    margin: 0 0 8px 0;
  }

  p {
    font-size: 15px;
    color: var(--oliver-text-muted);
    margin: 0;
    max-width: 300px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.load-more-section {
  display: flex;
  justify-content: center;
  padding: 12px 0 20px;
}

.load-more-button {
  padding: 8px 20px;
  border-radius: 8px;
  background: var(--oliver-indicator-bg);
  color: var(--oliver-accent);
  border: 1px solid var(--oliver-accent-border);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;

  &:hover:not(:disabled) {
    background: var(--oliver-accent-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .messages-list {
    padding: 16px 16px 0;
  }

  .load-more-button {
    padding: 10px 24px;
    font-size: 14px;
    min-width: 120px;
  }

  .loading-state {
    padding: 40px 20px;

    i {
      font-size: 36px;
    }

    p {
      font-size: 16px;
    }
  }
}

@container (max-width: 480px) {
  .messages-container {
    padding: 0 8px;
  }

  .messages-list {
    padding-left: 0;
    padding-right: 0;
  }

  .chat-input-wrap {
    padding-left: 8px;
    padding-right: 8px;
  }
}
</style>

<style lang="scss">
.chat-view {
  .specialist-panel {
    .specialist-name,
    .specialist-chevron,
    .specialist-markdown {
      color: var(--oliver-text-muted);
    }
    .specialist-markdown {
      h2,
      strong {
        color: var(--oliver-text);
      }
    }
  }
}
</style>
